/**
 * MaterialsPriceUpdateReviewPanel — staff HITL review of agent-proposed
 * materialsDatabase_hardware price updates (Rothoblaas / fasteners).
 *
 * Timber/glulam quotes auto-apply on propose; this panel is hardware-only
 * on the Materials Database Hardware tab.
 *
 * Side-by-side: supplier quote PDF (left) + proposed catalog diffs (right).
 * Approve applies via materialsPriceAgentApi (staff Firebase auth);
 * Reject leaves the catalog unchanged.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import {
  subscribePendingMaterialsPriceProposals,
  approveMaterialsPriceProposal,
  rejectMaterialsPriceProposal,
  resolveMaterialsQuoteUrl,
  formatPriceDiff,
  omittedUnchangedMaterialsPriceCount,
  visibleMaterialsPriceUpdates,
  MATERIALS_PRICE_PROPOSALS_INDEX_HINT,
  formatHardwareDiffTitle,
  type MaterialsPriceProposal,
  type MaterialsPriceUpdateDiff,
  type MaterialsCatalogDomain,
} from '../../utils/materialsPriceUpdateService';
import { Check, X, Loader2, FileText, ClipboardList, ChevronRight } from 'lucide-react';
import './MaterialsPriceUpdateReviewPanel.css';

function collectionLabel(domain: MaterialsCatalogDomain | undefined): string {
  if (domain === 'hardware') return 'materialsDatabase_hardware';
  if (domain === 'glulam') return 'materialsDatabase_glulam';
  return 'materialsDatabase_timber';
}

function DiffRow({ u, catalogDomain }: { u: MaterialsPriceUpdateDiff; catalogDomain?: MaterialsCatalogDomain }) {
  const isHardware =
    catalogDomain === 'hardware'
    || u.unitPrice != null
    || Boolean(u.sku)
    || (Boolean(u.name) && !u.species && !u.dimensions);

  if (isHardware) {
    const flipSupplier =
      u.flipPrimarySupplier === true
      && Boolean(u.existingSupplier)
      && Boolean(u.supplier)
      && u.existingSupplier !== u.supplier;
    const unitLabel = (u.unit || 'each').toLowerCase() === 'each' || (u.unit || '').toLowerCase() === 'ea'
      ? '$/ea'
      : `/${u.unit || 'ea'}`;
    return (
      <li className={`mpr-diff mpr-diff--${u.action}${flipSupplier ? ' mpr-diff--supplier-flip' : ''}`}>
        <div className="mpr-diff-head">
          <span className={`mpr-badge mpr-badge--${u.action}`}>
            {u.action === 'new' ? 'New' : 'Price change'}
          </span>
          {flipSupplier && (
            <span className="mpr-badge mpr-badge--supplier-flip">Primary supplier</span>
          )}
          <span className="mpr-diff-title">{formatHardwareDiffTitle(u)}</span>
          <span className="mpr-diff-moisture">{unitLabel}</span>
        </div>
        <div className="mpr-diff-prices">
          {u.action === 'update' ? (
            <>
              <span className="mpr-old">
                {formatPriceDiff(u.existingUnitPrice)}
                {u.existingSupplier ? ` (${u.existingSupplier})` : ''}
              </span>
              <ChevronRight size={12} />
              <span className="mpr-new">
                {formatPriceDiff(u.unitPrice)}
                {u.supplier ? ` (${u.supplier})` : ''}
              </span>
            </>
          ) : (
            <span className="mpr-new">
              {formatPriceDiff(u.unitPrice)}
              {u.supplier ? ` · ${u.supplier}` : ''}
            </span>
          )}
        </div>
        {flipSupplier && (
          <p className="mpr-supplier-flip" data-testid="mpr-supplier-flip">
            Primary supplier: <span className="mpr-old">{u.existingSupplier}</span>
            <ChevronRight size={11} aria-hidden />
            <span className="mpr-new">{u.supplier}</span>
          </p>
        )}
        {u.rationale && <p className="mpr-rationale">{u.rationale}</p>}
      </li>
    );
  }

  const showMoisture = catalogDomain !== 'glulam';
  const moisture =
    u.moistureContent === 'green'
      ? 'Green'
      : u.moistureContent === 'kiln_dried'
        ? 'Kiln dried'
        : '—';
  const flipSupplier =
    u.flipPrimarySupplier === true
    && Boolean(u.existingSupplier)
    && Boolean(u.supplier)
    && u.existingSupplier !== u.supplier;
  return (
    <li className={`mpr-diff mpr-diff--${u.action}${flipSupplier ? ' mpr-diff--supplier-flip' : ''}`}>
      <div className="mpr-diff-head">
        <span className={`mpr-badge mpr-badge--${u.action}`}>
          {u.action === 'new' ? 'New' : 'Price change'}
        </span>
        {flipSupplier && (
          <span className="mpr-badge mpr-badge--supplier-flip">Primary supplier</span>
        )}
        <span className="mpr-diff-title">
          {u.species || '—'} · {u.grade || '—'} · {u.dimensions || '—'}
        </span>
        {showMoisture && <span className="mpr-diff-moisture">{moisture}</span>}
      </div>
      <div className="mpr-diff-prices">
        {u.action === 'update' ? (
          <>
            <span className="mpr-old">
              {formatPriceDiff(u.existingPiecePrice)} / {formatPriceDiff(u.existingCostPerBdFt)}/bf
              {u.existingSupplier ? ` (${u.existingSupplier})` : ''}
            </span>
            <ChevronRight size={12} />
            <span className="mpr-new">
              {formatPriceDiff(u.piecePrice)} / {formatPriceDiff(u.costPerBdFt)}/bf
              {u.supplier ? ` (${u.supplier})` : ''}
            </span>
          </>
        ) : (
          <span className="mpr-new">
            {formatPriceDiff(u.piecePrice)} / {formatPriceDiff(u.costPerBdFt)}/bf
            {u.supplier ? ` · ${u.supplier}` : ''}
          </span>
        )}
      </div>
      {flipSupplier && (
        <p className="mpr-supplier-flip" data-testid="mpr-supplier-flip">
          Primary supplier: <span className="mpr-old">{u.existingSupplier}</span>
          <ChevronRight size={11} aria-hidden />
          <span className="mpr-new">{u.supplier}</span>
        </p>
      )}
      {u.rationale && <p className="mpr-rationale">{u.rationale}</p>}
    </li>
  );
}

export const MaterialsPriceUpdateReviewPanel: React.FC<{
  /** When set, only show proposals for this catalog domain (timber vs hardware). */
  catalogDomain?: MaterialsCatalogDomain;
}> = ({ catalogDomain }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const forceOpen =
    searchParams.get('tab') === 'price-reviews' ||
    searchParams.get('reviews') === '1' ||
    searchParams.get('priceReviews') === '1';

  const [proposals, setProposals] = useState<MaterialsPriceProposal[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [quoteUrl, setQuoteUrl] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    return subscribePendingMaterialsPriceProposals((list, meta) => {
      setLoading(false);
      setProposals(list);
      setSubError(meta?.error || null);
    });
  }, []);

  const filteredProposals = React.useMemo(() => {
    if (!catalogDomain) return proposals;
    return proposals.filter((p) => (p.catalogDomain || 'timber') === catalogDomain);
  }, [proposals, catalogDomain]);

  useEffect(() => {
    setSelectedId((prev) => {
      if (prev && filteredProposals.some((p) => p.id === prev)) return prev;
      return filteredProposals[0]?.id ?? null;
    });
  }, [filteredProposals]);

  useEffect(() => {
    if (forceOpen) {
      setCollapsed(false);
      const el = document.querySelector('[data-testid="materials-price-reviews"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [forceOpen, loading, filteredProposals.length, subError]);

  const selected = filteredProposals.find(p => p.id === selectedId) || null;
  const selectedDomain: MaterialsCatalogDomain = selected?.catalogDomain || catalogDomain || 'timber';
  const visibleUpdates = visibleMaterialsPriceUpdates(selected?.updates);
  const omittedUnchanged = omittedUnchangedMaterialsPriceCount(selected);

  useEffect(() => {
    let cancelled = false;
    if (!selected) {
      setQuoteUrl(null);
      return;
    }
    setQuoteLoading(true);
    resolveMaterialsQuoteUrl(selected)
      .then(url => {
        if (!cancelled) setQuoteUrl(url);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const handleApprove = useCallback(async () => {
    if (!selected?.id || !user) return;
    setBusyId(selected.id);
    setError(null);
    try {
      await approveMaterialsPriceProposal(selected.id, selectedDomain);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setBusyId(null);
    }
  }, [selected, selectedDomain, user]);

  const handleReject = useCallback(async () => {
    if (!selected?.id || !user) return;
    setBusyId(selected.id);
    setError(null);
    try {
      await rejectMaterialsPriceProposal(selected.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setBusyId(null);
    }
  }, [selected, user]);

  // Hide when this tab has no matching reviews (unless deep-linked or still loading / errored).
  if (
    !loading
    && !subError
    && !forceOpen
    && filteredProposals.length === 0
  ) {
    return null;
  }

  const domainHint =
    catalogDomain === 'hardware'
      ? 'Review hardware/fastener quote PDFs beside proposed materialsDatabase_hardware changes (Rothoblaas, etc.). Approve merges into the Hardware catalog and appends price history for Trend sparklines; Reject discards. Nothing auto-applies.'
      : catalogDomain === 'glulam'
        ? 'Review glulam supplier quote PDFs beside proposed materialsDatabase_glulam changes. Approve merges into the Glulams catalog and appends price history for Trend sparklines; Reject discards. Nothing auto-applies.'
        : catalogDomain === 'timber'
          ? 'Review timber supplier quote PDFs beside proposed materialsDatabase_timber changes. Approve merges into Materials Database and appends price history for Trend sparklines; Reject discards. Nothing auto-applies.'
          : 'Review the supplier quote PDF beside proposed timber, glulam, or hardware/fastener catalog changes. Approve merges into Materials Database and appends price history for Trend sparklines; Reject discards. Nothing auto-applies.';

  const busy = busyId === selected?.id;

  return (
    <section className="mpr-root" data-testid="materials-price-reviews" id="price-reviews">
      <header className="mpr-header">
        <button
          type="button"
          className="mpr-title-btn"
          onClick={() => setCollapsed(c => !c)}
          aria-expanded={!collapsed}
        >
          <ClipboardList size={16} />
          Price update reviews
          <span className="mpr-count">{loading ? '…' : filteredProposals.length}</span>
        </button>
        <p className="mpr-hint">
          {domainHint}
        </p>
      </header>

      {!collapsed && (
        <div className="mpr-body">
          {loading && (
            <div className="mpr-status">
              <Loader2 size={16} className="mpr-spin" /> Loading pending price updates…
            </div>
          )}

          {!loading && subError && (
            <div className="mpr-status mpr-status--error" role="alert">
              <strong>Could not load price update reviews.</strong>
              <span>{subError}</span>
              <span className="mpr-status-hint">{MATERIALS_PRICE_PROPOSALS_INDEX_HINT}</span>
            </div>
          )}

          {!loading && !subError && filteredProposals.length === 0 && (
            <div className="mpr-status">
              No pending price update proposals right now.
              <Link to="/supplier-rfqs" className="mpr-rfq-link">
                Open Supplier RFQs
              </Link>
            </div>
          )}

          {!loading && !subError && filteredProposals.length > 0 && (
            <>
              <aside className="mpr-queue">
                <ul>
                  {filteredProposals.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className={`mpr-queue-item${p.id === selectedId ? ' is-active' : ''}`}
                        onClick={() => setSelectedId(p.id)}
                      >
                        <FileText size={13} />
                        <span className="mpr-queue-name">{p.fileName || 'Quote'}</span>
                        <span className="mpr-queue-meta">
                          {p.catalogDomain === 'hardware' ? 'Hardware · ' : p.catalogDomain === 'glulam' ? 'Glulam · ' : ''}
                          {p.supplierName || p.supplierOrgName || 'Supplier'}
                          {(() => {
                            const n = visibleMaterialsPriceUpdates(p.updates).length;
                            if (n) return ` · ${n} change${n === 1 ? '' : 's'}`;
                            return omittedUnchangedMaterialsPriceCount(p)
                              ? ' · no price changes'
                              : '';
                          })()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <Link to="/supplier-rfqs" className="mpr-rfq-link">
                  Open Supplier RFQs
                </Link>
              </aside>

              {selected && (
                <div className="mpr-review">
                  <div className="mpr-pane mpr-pane--pdf">
                    <div className="mpr-pane-label">Supplier quote</div>
                    {quoteLoading && (
                      <div className="mpr-pdf-empty">
                        <Loader2 size={18} className="mpr-spin" /> Loading PDF…
                      </div>
                    )}
                    {!quoteLoading && quoteUrl && (
                      <iframe
                        title={selected.fileName || 'Supplier quote'}
                        src={quoteUrl}
                        className="mpr-pdf-frame"
                      />
                    )}
                    {!quoteLoading && !quoteUrl && (
                      <div className="mpr-pdf-empty">
                        Quote file unavailable
                        {selected.fileName ? ` (${selected.fileName})` : ''}.
                      </div>
                    )}
                  </div>

                  <div className="mpr-pane mpr-pane--diffs">
                    <div
                      className="mpr-pane-label"
                      data-testid="mpr-collection-label"
                      data-catalog-domain={selectedDomain}
                    >
                      Proposed changes to {collectionLabel(selectedDomain)}
                    </div>
                    {error && <div className="mpr-error">{error}</div>}
                    {selected.parseNotes && (
                      <p className="mpr-parse-notes">{selected.parseNotes}</p>
                    )}
                    {visibleUpdates.length === 0 && omittedUnchanged > 0 ? (
                      <p className="mpr-empty-diffs">
                        {selectedDomain === 'hardware'
                          ? 'All quoted SKUs already match the catalog (same unit price). Reject to dismiss this review.'
                          : 'All quoted SKUs already match the catalog (same piece price and $/bf). Reject to dismiss this review.'}
                      </p>
                    ) : selected.updates.length === 0 ? (
                      <p className="mpr-empty-diffs">
                        No SKU line items proposed (lump-total quotes cannot invent piece prices).
                        Reject or wait for a re-parse with line items.
                      </p>
                    ) : (
                      <>
                        {omittedUnchanged > 0 && (
                          <p className="mpr-parse-notes">
                            {omittedUnchanged} quoted SKU{omittedUnchanged === 1 ? '' : 's'} already
                            match catalog prices and are hidden.
                          </p>
                        )}
                        <ul className="mpr-diff-list">
                          {visibleUpdates.map((u, i) => (
                            <DiffRow
                              key={`${u.matchKey || u.sku || i}-${i}`}
                              u={u}
                              catalogDomain={selectedDomain}
                            />
                          ))}
                        </ul>
                      </>
                    )}
                    <div className="mpr-actions">
                      <button
                        type="button"
                        className="mpr-approve"
                        disabled={busy || !user || visibleUpdates.length === 0}
                        onClick={handleApprove}
                      >
                        {busy ? <Loader2 size={14} className="mpr-spin" /> : <Check size={14} />}
                        Approve (apply)
                      </button>
                      <button
                        type="button"
                        className="mpr-reject"
                        disabled={busy || !user}
                        onClick={handleReject}
                      >
                        <X size={14} />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default MaterialsPriceUpdateReviewPanel;
